import {
  Component,
  HostListener,
  signal,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  ViewChild,
  inject,
  computed,
  ChangeDetectionStrategy,
} from '@angular/core';

import { ItemSnapshot, takeGroupSnapshot, restoreGroupFromSnapshot } from './snapshots.engine';

import { reflowRowStable, reflowCascadeStable, decideSecondForRow, setSecondForBothLines, rowReservationsOnLane } from './reflow.engine';

import { PreviewMap, setPreview, clearPreview, filterByLaneWithPreview } from './preview-lane.engine';

import { otherType, getPairIds } from './pairing.engine';

import { CreateGestureDirective } from './create-gesture.directive';
import { CommonModule } from '@angular/common';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';

import {
  timelineWidthPx as timelineWidthPxUtil,
  minutesToPx as minutesToPxUtil,
  hourLeftPx as hourLeftPxUtil,
  minutesToLabel,
  snapInRange,
  snapMinute,
  startOfToday,
  defaultSlot,
} from 'src/app/utils/time.utils';

import { BookingActionsService } from './booking-actions.service';

import { Reservation, RowType, ApiReservation, mapApiToUiReservations, Aircraft, Student, User } from 'src/app/models';

import { BookingDialogComponent, BookingDialogData } from '../detail/booking-detail.component';
import { ConfirmDialogComponent, ConfirmDialogData } from '../confirm-dialog/confirm-dialog.component';
import { AircraftDetailComponent } from 'src/app/aircraft/detail/aircraft-detail.component';

import { AircraftService } from 'src/app/service/aircraft.service';
import { ReservationService } from 'src/app/service/reservation.service';
import { InstructorService } from 'src/app/service/instructor.service';
import { AuthService } from 'src/app/service/auth.service';
import { StudentService } from 'src/app/service/student.service';
import { AvailabilityService } from '../../service/availability.service';
import { BookingStore } from './booking.store';

/* -------------------------------------------------------------------------- */
/*                                Types locaux                                */
/* -------------------------------------------------------------------------- */
type ResizeEdge = 'start' | 'end';

type ResizeState = {
  id: string;
  edge: ResizeEdge;
  rowType: RowType;
  rowId: string;
  startMin: number;
  endMin: number;
  sheet: HTMLElement;
  inner: HTMLElement;
  initialStartMin: number;
  initialEndMin: number;
  initialSecond: boolean;
};

type DragState = {
  id: string;
  rowType: RowType;
  rowId: string;
  duration: number;
  grabOffset: number;
  sheet: HTMLElement;
  inner: HTMLElement;
  initialStartMin: number;
  initialEndMin: number;
  initialSecond: boolean;
  startClientX: number;
  isActive: boolean;
};

/* -------------------------------------------------------------------------- */
/*                                Composant                                   */
/* -------------------------------------------------------------------------- */
@Component({
  selector: 'app-booking',
  standalone: true,
  imports: [
    CreateGestureDirective,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatToolbarModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
  ],
  templateUrl: './booking.component.html',
  styleUrls: ['./booking.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BookingComponent implements AfterViewInit, OnDestroy {
  /* ---------------------------------- Store --------------------------------- */
  /** Store global (état et données du board) */
  readonly store = inject(BookingStore);

  /* ------------------------------ Références DOM ---------------------------- */
  /** Racine du board – utile pour calculer l’offset top pour le CSS */
  @ViewChild('boardRoot', { static: false })
  boardRoot?: ElementRef<HTMLElement>;

  @ViewChild('sheet', { static: true }) sheet!: ElementRef<HTMLDivElement>;

  /* -------------------------------- Services -------------------------------- */
  constructor(
    private actions: BookingActionsService,
    private studentService: StudentService,
    private reservationService: ReservationService,
    private aircraftService: AircraftService,
    private authService: AuthService,
    private instructorService: InstructorService,
    public availabilityService: AvailabilityService, // public pour usage direct dans le template
    private host: ElementRef<HTMLElement>,
    private dialog: MatDialog
  ) {
    this.bootstrapData();
  }

  /* ------------------------------ TrackBy helper ---------------------------- */
  /** TrackBy: identifie de manière stable une réservation */
  trackResById = (_: number, r: Reservation) => `${r.id}:${r.rowType}`;

  /* --------------------------------- Grille -------------------------------- */
  /** Largeur d'une heure en px (pilotée par variable CSS) */
  private _hourW = 100;
  /** Largeur de la colonne labels en px (pilotée par variable CSS) */
  private _labelW = 220;

  /** Getter exposé au template pour la largeur d’une heure */
  get hourWidth(): number {
    return this._hourW;
  }
  /** Getter exposé au template pour la largeur de la colonne de gauche */
  get labelWidth(): number {
    return this._labelW;
  }

  /** Largeur totale de la timeline en px (en fonction des heures d’ouverture) */
  timelineWidth() {
    return timelineWidthPxUtil(this.store.open, this.store.close, this.hourWidth);
  }

  /** Recalculer la taille de chaque case selon la largeur de l'écran disponible */
  recomputeHourWidth = () => {
    const el = this.sheet?.nativeElement;
    if (!el) return;

    const hours = this.store.hoursCount(); // nombre de colonnes affichées
    if (!hours) return;

    const available = el.clientWidth; // largeur visible côté droit
    // Si tu veux "forcer" le fit même si c'est très serré, ne mets pas de min.
    const w = Math.floor(available / hours);
    // const w = available / hours;

    // Optionnel : bornes de confort
    const MIN = 18; // ou 24 selon lisibilité
    const MAX = 60; // optionnel si tu veux éviter des colonnes géantes
    this._hourW = w < MIN ? MIN : Math.min(MAX, w);
  };

  /* ------------------------------- DnD / Resize ------------------------------ */
  /** Seuil (px) avant d’activer un drag (évite le click fantôme) */
  private readonly dragThresholdPx = 5;

  /** Empêche l’ouverture de l’éditeur juste après un drag */
  private suppressNextEditClick = false;

  /** État du redimensionnement en cours */
  resizeState = signal<ResizeState | null>(null);
  /** État du déplacement en cours */
  dragState = signal<DragState | null>(null);

  /** Mémoire du dernier X souris (drag) pour déterminer la direction */
  private dragLastX: number | null = null;
  /** Mémoire du dernier X souris (resize) pour déterminer la direction */
  private resizeLastX: number | null = null;
  /** Direction courante du drag: -1 / 0 / +1 */
  private dragDir: -1 | 0 | 1 = 0;
  /** Direction courante du resize: -1 / 0 / +1 */
  private resizeDir: -1 | 0 | 1 = 0;

  /** Mode de geste global (pratique pour CSS & logique) */
  private mode: 'none' | 'pan' | 'create' | 'drag' | 'resize' = 'none';

  /** Snapshots (avant modif) pour rollback sûrs */
  private snapshots = new Map<string, ItemSnapshot[]>();

  /** Carte de preview: force une résa à s’afficher en 2ᵉ ligne (pendant drag/resize) */
  private previewSecond = signal<PreviewMap>({});

  /** Sauvegarde l’overflow du body pour restauration à la sortie */
  private prevBodyOverflow = '';

  /* ==========================================================================
   *                            Cycle de vie / Layout
   * ========================================================================== */
  // private ro?: ResizeObserver;
  /** AfterViewInit: lit les variables CSS, calcule l’offset top, bloque le scroll body */
  ngAfterViewInit() {
    this.readCssVars();
    this.updateBoardTopVar();

    // iOS paysage (barres système) : double passe
    requestAnimationFrame(() => this.updateBoardTopVar());
    setTimeout(() => this.updateBoardTopVar(), 0);

    // 🔒 Seule la board scrolle
    this.prevBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // this.ro = new ResizeObserver(() => this.recomputeHourWidth());
    // this.ro.observe(this.sheet.nativeElement);
    this.recomputeHourWidth();
  }

  /** OnDestroy: restaure l’overflow du body */
  ngOnDestroy(): void {
    this.stopAutoScroll();
    document.body.style.overflow = this.prevBodyOverflow || '';

    // this.ro?.disconnect();
  }

  /** Recalcule les métriques au resize/orientation */
  @HostListener('window:resize')
  @HostListener('window:orientationchange')
  onViewportChange() {
    this.readCssVars();
    this.updateBoardTopVar();
    this.recomputeHourWidth();
  }

  /** Met à jour la CSS var --board-top (distance board ↔ viewport top) */
  private updateBoardTopVar() {
    const el = this.boardRoot?.nativeElement;
    if (!el) return;
    const top = el.getBoundingClientRect().top;
    this.host.nativeElement.style.setProperty('--board-top', `${Math.max(0, Math.round(top))}px`);
  }

  /** Relit les variables CSS (--hour-w / --label-w) et force un refresh si elles changent */
  private readCssVars() {
    const el = this.host.nativeElement;
    const cs = getComputedStyle(el);
    const hour = parseFloat(cs.getPropertyValue('--hour-w')) || this._hourW;
    const label = parseFloat(cs.getPropertyValue('--label-w')) || this._labelW;
    const round = (v: number) => Math.round(v * 1000) / 1000;
    const changed = round(hour) !== round(this._hourW) || round(label) !== round(this._labelW);
    this._hourW = hour;
    this._labelW = label;
    if (changed) this.store.setReservations([...this.store.reservations()]);
  }

  /** Applique/retire le style "gesturing" (CSS) au body/host */
  private setGestureUi(on: boolean) {
    document.body.classList.toggle('booking-gesturing', on);
    this.host.nativeElement.classList.toggle('gesturing', on);
  }

  /* ==========================================================================
   *                         Conversions & utilitaires
   * ========================================================================== */

  /** Minutes → libellé HH:mm (pour affichage) */
  toLabel(m: number) {
    return minutesToLabel(m);
  }

  /** Minutes absolues → position en px sur la timeline */
  minuteToPx(min: number) {
    return minutesToPxUtil(min, this.store.open, this.hourWidth);
  }

  /** Heure entière → position en px (départ de colonne) */
  hourLeftPx(h: number) {
    return hourLeftPxUtil(h, this.store.open, this.hourWidth);
  }

  /** Position curseur → minutes brutes (bornées entre open/close) */
  private posToMinutesRaw(ev: PointerEvent, sheet: HTMLElement, _inner?: HTMLElement) {
    const sheetRect = sheet.getBoundingClientRect();
    const baselineLeft = sheetRect.left; // la colonne labels est hors de ce conteneur
    let xViewport = ev.clientX - baselineLeft;
    if (xViewport < 0) xViewport = 0;

    const x = xViewport + sheet.scrollLeft;
    const minutesFromOpen = x * (60 / this.hourWidth);
    const m = this.store.open * 60 + minutesFromOpen;
    const minOpen = this.store.open * 60;
    const maxClose = this.store.close * 60;
    return Math.min(maxClose, Math.max(minOpen, m));
  }

  /** Classes visuelles pour une ligne avion (ex: maintenance, grounded) */
  rowStripeClass(a: { condition?: string }): string[] {
    if (a?.condition === 'GROUNDED') return ['row-hatched', 'grounded'];
    if (a?.condition === 'MAINTENANCE_DUE') return ['row-hatched', 'maintenance'];
    return [];
  }

  /* ==========================================================================
   *                           Snapshots & Preview
   * ========================================================================== */

  /** Sauvegarde un snapshot du groupe lié à la résa (pour rollback) */
  private snapshotGroup(resId: string): void {
    const shots = takeGroupSnapshot(this.store.reservations(), resId);
    if (shots.length) this.snapshots.set(resId, shots);
  }

  /** Restaure un groupe depuis le dernier snapshot disponible */
  private restoreGroup(resId: string): void {
    const shots = this.snapshots.get(resId);
    if (!shots?.length) return;
    const next = restoreGroupFromSnapshot(this.store.reservations(), resId, shots);
    this.store.setReservations(next);
    this.resetPreviewFlag(resId);
    this.snapshots.delete(resId);
  }

  /** Applique le flag de preview (seconde ligne forcée) pour un couple (id,type) */
  private applyPreviewFlag(id: string, rowType: RowType, value: boolean) {
    this.previewSecond.set(setPreview(this.previewSecond(), id, rowType, value));
  }

  /** Réinitialise la preview (pour un id donné ou toute la map) */
  private resetPreviewFlag(id?: string) {
    this.previewSecond.set(clearPreview(this.previewSecond(), id));
  }

  /* ==========================================================================
   *                          Sélection via directive
   * ========================================================================== */

  /** Début d’une sélection (création) déclenchée par la directive */
  onSelectBegin(e: { rowType: RowType; rowId: string; startMin: number; endMin: number }) {
    this.store.selection.set({
      id: 'sel',
      rowType: e.rowType,
      rowId: e.rowId,
      startMin: e.startMin,
      endMin: e.endMin,
      isSolo: false,
      secondPosition: false,
    });
    this.store.selecting.set(true);
  }

  /** Mise à jour d’une sélection en cours (reste dans la plage [open..close]) */
  onSelectChange(e: { rowType: RowType; rowId: string; startMin: number; endMin: number }) {
    const sel = this.store.selection();
    if (!sel) return;

    const start = Math.max(this.store.open * 60, Math.min(e.startMin, this.store.close * 60));
    const end = Math.max(this.store.open * 60, Math.min(e.endMin, this.store.close * 60));
    this.store.selection.set({ ...sel, startMin: start, endMin: end });
  }

  /** Fin d’une sélection: ouvre le dialog de création si aucun conflit */
  onSelectCommit() {
    if (!this.store.selecting()) return;

    const sel = this.store.selection();
    this.store.selecting.set(false);
    this.store.selection.set(null);
    if (!sel) return;

    const start = Math.max(this.store.open * 60, Math.min(sel.startMin, this.store.close * 60 - this.store.step));
    const end = Math.max(start + this.store.step, Math.min(sel.endMin, this.store.close * 60));
    if (end - start < this.store.step) return;

    if (this.overlapsAnyOnLane(sel.rowType, sel.rowId, start, end, /* laneSecond= */ false)) return;

    const data = this.buildDialogData({
      baseId: sel.id,
      rowType: sel.rowType,
      rowId: sel.rowId,
      startMin: start,
      endMin: end,
      update: false,
    });

    const ref = this.dialog.open(BookingDialogComponent, {
      width: 'auto',
      height: 'auto',
      panelClass: 'full-screen-dialog',
      data,
    });

    ref.afterClosed().subscribe((result) => {
      if (result?.action !== 'save') return;
      this.handleCreateFromDialogResult(result);
    });
  }

  /** Annule la sélection en cours */
  onSelectCancel() {
    if (this.store.selecting()) {
      this.store.selecting.set(false);
      this.store.selection.set(null);
    }
  }

  /* ==========================================================================
   *                            Drag / Resize (live)
   * ========================================================================== */

  /** Début d’un redimensionnement (poignée gauche/droite) */
  onResizePointerDown(ev: PointerEvent, r: Reservation, edge: ResizeEdge, sheet: HTMLElement, inner: HTMLElement) {
    ev.preventDefault();
    ev.stopPropagation();
    if (!this.canEditReservation(r)) return;
    if (this.store.dragging() || this.store.resizing()) return;

    // capture sur un noeud stable (le host du composant)
    (this.host.nativeElement as any)?.setPointerCapture?.(ev.pointerId);

    this.snapshotGroup(r.id);

    this.store.resizing.set(true);
    this.resetPreviewFlag(r.id);
    this.resizeState.set({
      id: r.id,
      edge,
      rowType: r.rowType,
      rowId: r.rowId,
      startMin: r.startMin,
      endMin: r.endMin,
      sheet,
      inner,
      initialStartMin: r.startMin,
      initialEndMin: r.endMin,
      initialSecond: !!r.secondPosition,
    });

    document.body.classList.add('resizing');
    this.mode = 'resize';
    this.setGestureUi(true);

    this.resizeLastX = ev.clientX;
    this.resizeDir = 0;
  }

  /** Début d’un déplacement (drag sur la carte) */
  onDragPointerDown(ev: PointerEvent, r: Reservation, sheet: HTMLElement, inner: HTMLElement) {
    ev.stopPropagation();
    if (!this.canEditReservation(r)) return;
    if (this.store.dragging() || this.store.resizing() || this.store.selecting()) return;

    // capture sur un noeud stable (le host du composant)
    (this.host.nativeElement as any)?.setPointerCapture?.(ev.pointerId);

    this.snapshotGroup(r.id);

    const minuteRaw = this.posToMinutesRaw(ev, sheet, inner);
    const grabOffset = minuteRaw - r.startMin;

    this.store.dragging.set(true);
    this.resetPreviewFlag(r.id);
    this.dragState.set({
      id: r.id,
      rowType: r.rowType,
      rowId: r.rowId,
      duration: r.endMin - r.startMin,
      grabOffset,
      sheet,
      inner,
      initialStartMin: r.startMin,
      initialEndMin: r.endMin,
      startClientX: ev.clientX,
      isActive: false,
      initialSecond: !!r.secondPosition,
    });

    this.dragLastX = ev.clientX;
    this.dragDir = 0;
  }

  /** PointerMove global: met à jour drag/resize en temps réel */
  @HostListener('window:pointermove', ['$event'])
  onWindowPointerMove(ev: PointerEvent) {
    if (this.store.resizing()) {
      this.handleResizeOrDragLive('resize', ev);
      return;
    }
    if (this.store.dragging()) {
      this.handleResizeOrDragLive('drag', ev);
      return;
    }
  }

  /** PointerUp global: commit du drag/resize (persistence via service + reflow) */
  @HostListener('window:pointerup', ['$event'])
  onWindowPointerUpGlobal(_ev: PointerEvent) {
    (this.host.nativeElement as any)?.releasePointerCapture?.(_ev.pointerId);

    this.stopAutoScroll();

    // Fin de resize
    if (this.store.resizing()) {
      const st = this.resizeState();
      this.store.resizing.set(false);
      this.resizeState.set(null);
      document.body.classList.remove('resizing');
      this.mode = 'none';
      this.setGestureUi(false);
      if (!st) return;

      const after = this.store.reservations().find((x) => x.id === st.id);
      if (!after) return;

      this.actions.confirmAndPersistMoveOrResizeFlow({
        id: st.id,
        rowType: st.rowType,
        startMin: after.startMin,
        endMin: after.endMin,
        actionLabel: 'Resize booking',
        confirmBtn: 'Save',
        reason: 'resize',
        decideSecondForRow: (rowType, rowId, startMin, endMin, excludeId) =>
          decideSecondForRow(this.store.reservations(), rowType, rowId, startMin, endMin, excludeId),
        setSecondForBothLines: (resId, activeType, secondActive, secondOther) => {
          const next = setSecondForBothLines(this.store.reservations(), resId, activeType, secondActive, secondOther);
          this.store.setReservations(next);
        },
        clearPreview: this.resetPreviewFlag.bind(this),
        reflowCascadeStable: (activeRowType, activeRowId, preferId) => {
          const { next, flips } = reflowCascadeStable(this.store.reservations(), activeRowType, activeRowId, preferId);
          this.store.setReservations(next);
          return flips;
        },
        onRollback: () => {
          this.restoreGroup(st.id);
          const { next } = reflowRowStable(this.store.reservations(), st.rowType, st.rowId, st.id);
          this.store.setReservations(next);
          this.mode = 'none';
          this.setGestureUi(false);
        },
        refreshView: (reason) => {
          this.refreshView(reason);
          this.mode = 'none';
          this.setGestureUi(false);
        },
      });

      return;
    }

    // Fin de drag
    if (this.store.dragging()) {
      const st = this.dragState();
      this.store.dragging.set(false);
      this.dragState.set(null);
      document.body.classList.remove('dragging');

      if (!st) return;

      if (!st.isActive) {
        // ✨ Fallback clic: ouvre l’éditeur si pas de drag
        const r = this.store.reservations().find((x) => x.id === st.id && x.rowType === st.rowType);
        if (r) {
          // Empêche un éventuel click natif (s’il arrive) d’ouvrir une 2e fois :
          this.suppressNextEditClick = true;
          this.editReservation(r);
        }

        this.snapshots.delete(st.id);
        this.mode = 'none';
        this.setGestureUi(false);
        return;
      }

      const moved = this.store.reservations().find((x) => x.id === st.id);
      if (!moved) return;

      this.actions.confirmAndPersistMoveOrResizeFlow({
        id: st.id,
        rowType: st.rowType,
        startMin: moved.startMin,
        endMin: moved.endMin,
        actionLabel: 'Move booking',
        confirmBtn: 'Move',
        reason: 'move',
        decideSecondForRow: (rowType, rowId, startMin, endMin, excludeId) =>
          decideSecondForRow(this.store.reservations(), rowType, rowId, startMin, endMin, excludeId),
        setSecondForBothLines: (resId, activeType, secondActive, secondOther) => {
          const next = setSecondForBothLines(this.store.reservations(), resId, activeType, secondActive, secondOther);
          this.store.setReservations(next);
        },
        clearPreview: this.resetPreviewFlag.bind(this),
        reflowCascadeStable: (activeRowType, activeRowId, preferId) => {
          const { next, flips } = reflowCascadeStable(this.store.reservations(), activeRowType, activeRowId, preferId);
          this.store.setReservations(next);
          return flips;
        },
        onRollback: () => {
          this.restoreGroup(st.id);
          const { next } = reflowRowStable(this.store.reservations(), st.rowType, st.rowId, st.id);
          this.store.setReservations(next);
          this.mode = 'none';
          this.setGestureUi(false);
        },
        refreshView: (reason) => {
          this.refreshView(reason);
          this.mode = 'none';
          this.setGestureUi(false);
        },
      });

      this.mode = 'none';
      this.setGestureUi(false);
      return;
    }
  }

  /** PointerCancel global: rollback des gestes et reset des états */
  @HostListener('window:pointercancel', ['$event'])
  onWindowPointerCancelGlobal(_ev: PointerEvent) {
    (this.host.nativeElement as any)?.releasePointerCapture?.(_ev.pointerId);

    this.stopAutoScroll();

    // Cancel resize
    if (this.store.resizing()) {
      const st = this.resizeState();
      this.store.resizing.set(false);
      this.resizeState.set(null);
      document.body.classList.remove('resizing');
      if (st) {
        this.restoreGroup(st.id);
        const { next } = reflowRowStable(this.store.reservations(), st.rowType, st.rowId, st.id);
        this.store.setReservations(next);
      }
      this.mode = 'none';
      this.setGestureUi(false);
      return;
    }

    // Cancel drag
    if (this.store.dragging()) {
      const st = this.dragState();
      this.store.dragging.set(false);
      this.dragState.set(null);
      document.body.classList.remove('dragging');
      if (st) {
        this.restoreGroup(st.id);
        const { next } = reflowRowStable(this.store.reservations(), st.rowType, st.rowId, st.id);
        this.store.setReservations(next);
      }
      this.mode = 'none';
      this.setGestureUi(false);
      return;
    }

    // Cancel selection
    if (this.store.selecting()) {
      this.store.selecting.set(false);
      this.store.selection.set(null);
    }
    this.mode = 'none';
    this.setGestureUi(false);
  }

  /** Mise à jour live du drag/resize (snap grille, autoscroll, preview seconde ligne) */
  private handleResizeOrDragLive(kind: 'resize' | 'drag', ev: PointerEvent) {
    // --- Resize ---
    if (kind === 'resize') {
      ev.preventDefault();
      const st = this.resizeState();
      if (!st) return;

      if (this.resizeLastX != null) {
        const dx = ev.clientX - this.resizeLastX;
        if (dx !== 0) this.resizeDir = dx < 0 ? -1 : 1;
      }
      this.resizeLastX = ev.clientX;

      const minuteRaw = this.posToMinutesRaw(ev, st.sheet, st.inner);
      const minDur = this.store.step;
      const minOpen = this.store.open * 60;
      const maxClose = this.store.close * 60;

      let start = st.startMin;
      let end = st.endMin;

      if (st.edge === 'start') {
        let cand = snapInRange(minuteRaw, this.resizeDir, this.store.step);
        cand = Math.max(minOpen, Math.min(cand, end - minDur));
        start = cand;
      } else {
        let cand = snapInRange(minuteRaw, this.resizeDir, this.store.step);
        cand = Math.min(maxClose, Math.max(cand, start + minDur));
        end = cand;
      }

      this.resizeState.set({ ...st, startMin: start, endMin: end });
      this.applyPreviewForBoth(st.id, st.rowType, start, end);
      this.updateTimesInState(st.id, start, end);

      this.maybeAutoScroll(ev, st.sheet, () => {
        const cur = this.resizeState();
        if (!cur) return;
        this.updateResizeByClientX(ev.clientX, cur);
      });
      return;
    }

    // --- Drag ---
    const st = this.dragState();
    if (!st) return;

    if (!st.isActive) {
      const dx = Math.abs(ev.clientX - st.startClientX);
      if (dx < this.dragThresholdPx) return;

      this.suppressNextEditClick = true;
      this.dragState.set({ ...st, isActive: true });
      document.body.classList.add('dragging');
      this.mode = 'drag';
      this.setGestureUi(true);
    }

    ev.preventDefault();

    if (this.dragLastX != null) {
      const dx = ev.clientX - this.dragLastX;
      if (dx !== 0) this.dragDir = dx < 0 ? -1 : 1;
    }
    this.dragLastX = ev.clientX;

    const minuteRaw = this.posToMinutesRaw(ev, st.sheet, st.inner);
    const minOpen = this.store.open * 60;
    const maxClose = this.store.close * 60;
    const duration = st.duration;

    let newStart = snapInRange(minuteRaw - st.grabOffset, this.dragDir, this.store.step);
    newStart = Math.max(minOpen, Math.min(newStart, maxClose - duration));
    const newEnd = newStart + duration;

    this.applyPreviewForBoth(st.id, st.rowType, newStart, newEnd);
    this.updateTimesInState(st.id, newStart, newEnd);

    this.maybeAutoScroll(ev, st.sheet, () => {
      const cur = this.dragState();
      if (!cur) return;
      this.updateDragByClientX(ev.clientX, cur);
    });
  }

  /** Applique les nouveaux start/end d’une résa dans le state */
  private updateTimesInState(id: string, startMin: number, endMin: number) {
    this.store.setReservations(this.store.reservations().map((x) => (x.id === id ? { ...x, startMin, endMin } : x)));
  }

  /** Calcule et applique la preview 2ᵉ ligne côté actif & côté ‘pair’ */
  private applyPreviewForBoth(id: string, rowType: RowType, startMin: number, endMin: number) {
    const { aircraftId, instructorId } = getPairIds(this.store.reservations(), id);

    const secondActive = decideSecondForRow(
      this.store.reservations(),
      rowType,
      rowType === 'aircraft' ? String(aircraftId) : String(instructorId),
      startMin,
      endMin,
      id
    );
    this.applyPreviewFlag(id, rowType, secondActive);

    const otherT = otherType(rowType);
    const otherId = otherT === 'aircraft' ? aircraftId : instructorId;

    if (otherId) {
      const secondOther = decideSecondForRow(this.store.reservations(), otherT, String(otherId), startMin, endMin, id);
      this.applyPreviewFlag(id, otherT, secondOther);
    }
  }

  /** Mise à jour du drag par un clientX (utile pendant autoscroll) */
  private updateDragByClientX(clientX: number, st: DragState) {
    const fakeEv = { clientX } as PointerEvent;
    const minuteRaw = this.posToMinutesRaw(fakeEv, st.sheet, st.inner);
    const minOpen = this.store.open * 60;
    const maxClose = this.store.close * 60;
    const duration = st.duration;

    let newStart = snapInRange(minuteRaw - st.grabOffset, this.dragDir, this.store.step);
    newStart = Math.max(minOpen, Math.min(newStart, maxClose - duration));
    const newEnd = newStart + duration;

    this.applyPreviewForBoth(st.id, st.rowType, newStart, newEnd);
    this.updateTimesInState(st.id, newStart, newEnd);
  }

  /** Mise à jour du resize par un clientX (utile pendant autoscroll) */
  private updateResizeByClientX(clientX: number, st: ResizeState) {
    const fakeEv = { clientX } as PointerEvent;
    const minuteRaw = this.posToMinutesRaw(fakeEv, st.sheet, st.inner);
    const minDur = this.store.step;
    const minOpen = this.store.open * 60;
    const maxClose = this.store.close * 60;

    let start = st.startMin;
    let end = st.endMin;

    if (st.edge === 'start') {
      let cand = snapInRange(minuteRaw, this.resizeDir, this.store.step);
      cand = Math.max(minOpen, Math.min(cand, end - minDur));
      start = cand;
    } else {
      let cand = snapInRange(minuteRaw, this.resizeDir, this.store.step);
      cand = Math.min(maxClose, Math.max(cand, start + minDur));
      end = cand;
    }

    this.resizeState.set({ ...st, startMin: start, endMin: end });
    this.applyPreviewForBoth(st.id, st.rowType, start, end);
    this.updateTimesInState(st.id, start, end);
  }

  /* ==========================================================================
   *                                Autoscroll
   * ========================================================================== */

  /** RAF id en cours pour l’autoscroll */
  private autoScrollRAF: number | null = null;
  /** Direction d’autoscroll: -1 / 0 / +1 */
  private autoScrollDir: -1 | 0 | 1 = 0;
  /** Contexte courant d’autoscroll */
  private lastAutoCtx?: {
    clientX: number;
    sheet: HTMLElement;
    update: () => void;
    leftTrigger: number;
    rightTrigger: number;
  };

  /** Déclenche/arrête l’autoscroll si proche d’un bord du conteneur horizontal */
  private maybeAutoScroll(ev: PointerEvent, sheet: HTMLElement, update: () => void) {
    const rect = sheet.getBoundingClientRect();
    const EDGE = 48;
    const baselineLeft = rect.left;
    const leftTrigger = baselineLeft + EDGE;
    const rightTrigger = rect.right - EDGE;

    if (ev.clientX > rightTrigger) {
      this.startAutoScroll(+1, ev.clientX, sheet, update, leftTrigger, rightTrigger);
    } else if (ev.clientX < leftTrigger) {
      this.startAutoScroll(-1, ev.clientX, sheet, update, leftTrigger, rightTrigger);
    } else {
      this.stopAutoScroll();
    }
  }

  /** Lance une boucle RAF pour scroller automatiquement pendant le geste */
  private startAutoScroll(dir: -1 | 1, clientX: number, sheet: HTMLElement, update: () => void, leftTrigger: number, rightTrigger: number) {
    this.autoScrollDir = dir;
    this.lastAutoCtx = { clientX, sheet, update, leftTrigger, rightTrigger };
    if (this.autoScrollRAF) return;

    const tick = () => {
      if (!this.lastAutoCtx || this.autoScrollDir === 0) {
        this.stopAutoScroll();
        return;
      }
      const { sheet, update } = this.lastAutoCtx;
      const speed = 8; // vitesse douce (iOS-friendly)
      sheet.scrollLeft += dir * speed;

      update();
      this.autoScrollRAF = requestAnimationFrame(tick);
    };

    this.autoScrollRAF = requestAnimationFrame(tick);
  }

  /** Stoppe l’autoscroll en cours */
  private stopAutoScroll() {
    if (this.autoScrollRAF) cancelAnimationFrame(this.autoScrollRAF);
    this.autoScrollRAF = null;
    this.autoScrollDir = 0;
    this.lastAutoCtx = undefined;
  }

  /* ==========================================================================
   *                             Anti-overlap helpers
   * ========================================================================== */

  /** Vrai s’il existe un chevauchement sur la lane donnée (primaire/secondaire) */
  private overlapsAnyOnLane(rowType: RowType, rowId: string, start: number, end: number, laneSecond: boolean, excludeId?: string) {
    return rowReservationsOnLane(this.store.reservations(), rowType, rowId, laneSecond).some(
      (r) => r.id !== excludeId && start < r.endMin && end > r.startMin
    );
  }

  /** Empêche de traverser une résa existante pendant la création (sur la 1ʳᵉ ligne uniquement) */
  clampPointerForCreate(rowType: RowType, rowId: string, anchor: number, pointer: number) {
    const list = rowReservationsOnLane(this.store.reservations(), rowType, rowId, false);
    const openMin = this.store.open * 60;
    const closeMin = this.store.close * 60;

    if (pointer >= anchor) {
      let limit = closeMin;
      for (const r of list) {
        if (r.startMin >= anchor && r.startMin < limit) limit = r.startMin;
        if (anchor >= r.startMin && anchor < r.endMin) return anchor;
      }
      return Math.min(pointer, limit);
    } else {
      let limit = openMin;
      for (const r of list) {
        if (r.endMin <= anchor && r.endMin > limit) limit = r.endMin;
        if (anchor >= r.startMin && anchor < r.endMin) return anchor;
      }
      return Math.max(pointer, limit);
    }
  }

  /* ==========================================================================
   *                       CRUD / Dialogs / Navigation
   * ========================================================================== */

  /** Ouvre le détail d’un avion dans un dialog plein écran */
  protected openAircraft(a: Aircraft) {
    this.dialog.open(AircraftDetailComponent, {
      width: 'auto',
      height: 'auto',
      panelClass: 'full-screen-dialog',
      data: { aircraft: a },
      autoFocus: false,
      restoreFocus: false,
    });
  }

  /** Click sur une carte: ouvre le dialog d’édition si autorisé */
  onReservationClick(r: Reservation, ev?: Event) {
    if (this.suppressNextEditClick) {
      this.suppressNextEditClick = false;
      ev?.stopPropagation();
      return;
    }
    if (this.canEditReservation(r)) this.editReservation(r, ev);
  }

  /** Stoppe la propagation (utile sur les boutons dans une carte draggable) */
  squelch(ev: Event) {
    ev.preventDefault();
    ev.stopPropagation();
  }

  /** Click ‘×’ sur une carte: confirme puis supprime la réservation */
  onDeleteClick(r: Reservation, ev?: Event) {
    this.squelch(ev!);
    this.confirm$({
      title: 'Delete booking',
      message: 'Confirm deletion ?',
      confirmText: 'Delete',
    }).subscribe((ok) => {
      if (!ok) return;
      // délègue toute la logique (suppression + reflow + persist flips)
      this.actions.deleteReservationFlow({
        resId: r.id,
        refreshView: (reason) => this.refreshView(reason),
      });
    });
  }

  /** Dialog de confirmation générique */
  private confirm$(data: ConfirmDialogData) {
    return this.dialog.open(ConfirmDialogComponent, { data, width: '420px' }).afterClosed();
  }

  /** Bouton “Création rapide”: ouvre le dialog prérempli sur le 1er avion dispo */
  openQuickCreate() {
    const { startMin, endMin } = defaultSlot(new Date(), this.store.open, this.store.close, this.store.step);
    const data = this.buildDialogData({
      baseId: 'sel',
      rowType: 'aircraft',
      rowId: this.store.aircrafts()[0]?.id ?? '',
      startMin,
      endMin,
      update: false,
    });

    const ref = this.dialog.open(BookingDialogComponent, {
      width: 'auto',
      height: 'auto',
      panelClass: 'full-screen-dialog',
      data,
    });
    ref.afterClosed().subscribe((result) => {
      if (result?.action !== 'save') return;
      this.handleCreateFromDialogResult(result);
    });
  }

  /** Construit les données d’entrée pour le dialog (create/update) */
  private buildDialogData(args: {
    baseId: string;
    rowType: RowType;
    rowId: string;
    startMin: number;
    endMin: number;
    update: boolean;
    currentAircraftId?: string | undefined;
    currentInstructorId?: string | undefined;
    currentStudentId?: string | undefined;
    createdBy?: User;
    createdAt?: Date;
    updatedAt?: Date;
    secondPosition?: boolean;
  }): BookingDialogData {
    const rowLabel =
      args.rowType === 'aircraft'
        ? this.store.aircrafts().find((a) => a.id === args.rowId)?.code ?? args.rowId
        : this.store.instructors().find((i) => i.id === args.rowId)?.name ?? args.rowId;

    return {
      id: args.baseId,
      rowType: args.rowType,
      rowId: args.rowId,
      rowLabel,
      startMin: args.startMin,
      endMin: args.endMin,
      openMin: this.store.open * 60,
      closeMin: this.store.close * 60,
      step: this.store.step,
      aircraft: this.store.aircrafts(),
      instructors: this.store.instructors(),
      students: this.store.students(),
      currentDate: this.store.currentDate(),
      aircraftId: args.currentAircraftId,
      instructorId: args.currentInstructorId,
      studentId: args.currentStudentId,
      createdBy: args.createdBy,
      createdAt: args.createdAt,
      updatedAt: args.updatedAt,
      secondPosition: !!args.secondPosition,
      update: args.update,
    } as any;
  }

  /** Traite et persiste une création issue du dialog */
  private handleCreateFromDialogResult(result: any) {
    this.actions.createFromDialogFlow(
      result,
      (rowType, rowId, startMin, endMin, excludeId) => decideSecondForRow(this.store.reservations(), rowType, rowId, startMin, endMin, excludeId),
      (activeRowType, activeRowId, preferId) => {
        const { next, flips } = reflowCascadeStable(this.store.reservations(), activeRowType, activeRowId, preferId);
        this.store.setReservations(next);
        return flips;
      },
      (reason) => this.refreshView(reason)
    );
  }

  /** Ouvre le dialog d’édition d’une résa existante */
  editReservation(r: Reservation, ev?: Event) {
    ev?.stopPropagation();

    const currentAircraftId =
      this.store.reservations().find((x) => x.id === r.id && x.rowType === 'aircraft')?.rowId ?? (r.rowType === 'aircraft' ? r.rowId : undefined);

    const currentInstructorId =
      this.store.reservations().find((x) => x.id === r.id && x.rowType === 'instructor')?.rowId ?? (r.rowType === 'instructor' ? r.rowId : undefined);

    const currentStudentId = this.store.reservations().find((x) => x.id === r.id).student?.id ?? null;

    const data = this.buildDialogData({
      baseId: r.id,
      rowType: r.rowType,
      rowId: r.rowId,
      startMin: r.startMin,
      endMin: r.endMin,
      update: true,
      currentAircraftId,
      currentInstructorId,
      currentStudentId,
      createdBy: r.createdBy,
      createdAt: r.createdAt,
      updatedAt: r.updatedAt,
      secondPosition: !!this.store.reservations().find((x) => x.id === r.id)?.secondPosition,
    });

    this.actions.editReservationFlow({
      res: r,
      dialogData: data,
      decideSecondForRow: (rowType, rowId, startMin, endMin, excludeId) =>
        decideSecondForRow(this.store.reservations(), rowType, rowId, startMin, endMin, excludeId),
      reflowCascadeStable: (activeRowType, activeRowId, preferId) => {
        const { next, flips } = reflowCascadeStable(this.store.reservations(), activeRowType, activeRowId, preferId);
        this.store.setReservations(next);
        return flips;
      },
      onRollback: (prev, prevAircraftId, prevInstructorId) => {
        this.store.setReservations(prev);
        if (prevAircraftId) {
          const { next } = reflowRowStable(this.store.reservations(), 'aircraft', String(prevAircraftId), r.id);
          this.store.setReservations(next);
        }
        if (prevInstructorId) {
          const { next } = reflowRowStable(this.store.reservations(), 'instructor', String(prevInstructorId), r.id);
          this.store.setReservations(next);
        }
      },
      refreshView: (reason) => this.refreshView(reason),
    });
  }

  /** Vrai si l’utilisateur courant peut modifier la résa (créateur/instructeur/élève) */
  private canEditReservation(r: Reservation): boolean {
    const meId = this.authService.currentUser?.id;
    if (!meId) return false;
    return r.createdBy?.id === meId || r.instructor?.user.id === meId || r.student?.user.id === meId;
  }

  /* ==========================================================================
   *                            Data / Bootstrap
   * ========================================================================== */

  /** Charge les ressources et les réservations du jour courant */
  private bootstrapData() {
    this.aircraftService.getAircrafts().subscribe((a) => this.store.aircrafts.set(a as any));
    this.instructorService.getInstructors().subscribe((list) => {
      this.store.instructors.set(list as any);
    });
    this.studentService.getStudents?.().subscribe?.((s: any[]) => this.store.students.set(s as Student[]));
    this.loadReservationsForDay(this.store.currentDate());
  }

  /** Charge les réservations pour une journée [open..close] donnée */
  private loadReservationsForDay(day: Date) {
    const from = new Date(day);
    from.setHours(this.store.open, 0, 0, 0);
    const to = new Date(day);
    to.setHours(this.store.close, 0, 0, 0);

    this.reservationService.getReservations(from.toISOString(), to.toISOString()).subscribe((list: ApiReservation[]) => {
      this.store.setReservations(mapApiToUiReservations(list));
    });
  }

  /** Rafraîchit la vue (re-fetch des réservations du jour courant) */
  private refreshView(_reason?: string) {
    this.loadReservationsForDay(this.store.currentDate());
  }

  /* ==========================================================================
   *                          Navigation dans les jours
   * ========================================================================== */

  /** Changement de date via le datepicker */
  onPick(date: Date | null) {
    if (!date) return;
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    this.store.currentDate.set(d);
    this.loadReservationsForDay(d);
  }

  /** Jour précédent */
  previousDay() {
    const prev = new Date(this.store.currentDate());
    prev.setDate(prev.getDate() - 1);
    this.store.currentDate.set(prev);
    this.loadReservationsForDay(prev);
  }

  /** Jour suivant */
  nextDay() {
    const next = new Date(this.store.currentDate());
    next.setDate(next.getDate() + 1);
    this.store.currentDate.set(next);
    this.loadReservationsForDay(next);
  }

  /** Revenir à aujourd’hui */
  goToday() {
    const today = startOfToday();
    this.store.currentDate.set(today);
    this.loadReservationsForDay(today);
  }

  /* ==========================================================================
   *                           Sélecteurs pour le template
   * ========================================================================== */

  /** Vrai si la sélection en cours cible ce (type,id) */
  hasSelectionFor(type: RowType, id: string) {
    const s = this.store.selection();
    return !!s && s.rowType === type && s.rowId === id;
  }

  /** Début de la sélection (minutes) */
  selStart() {
    return this.store.selection()?.startMin ?? 0;
  }

  /** Fin de la sélection (minutes) */
  selEnd() {
    return this.store.selection()?.endMin ?? 0;
  }

  /** Résas avion (lane primaire) avec preview intégrée */
  reservationsForAircraftPrimary(id: string) {
    return filterByLaneWithPreview(this.store.reservations(), 'aircraft', id, false, this.previewSecond());
  }

  /** Résas avion (lane secondaire) avec preview intégrée */
  reservationsForAircraftSecond(id: string) {
    return filterByLaneWithPreview(this.store.reservations(), 'aircraft', id, true, this.previewSecond());
  }

  /** Résas instructeur (lane primaire) avec preview intégrée */
  reservationsForInstructorPrimary(id: string) {
    return filterByLaneWithPreview(this.store.reservations(), 'instructor', id, false, this.previewSecond());
  }

  /** Résas instructeur (lane secondaire) avec preview intégrée */
  reservationsForInstructorSecond(id: string) {
    return filterByLaneWithPreview(this.store.reservations(), 'instructor', id, true, this.previewSecond());
  }

  displayParticipants(r: Reservation) {
    const me = this.authService.currentUser;
    const creator = r.createdBy;
    const instructor = r.instructor;
    const student = r.student;
    let first = '';
    let second = '';
    let returnString = this.toLabel(r.startMin) + ' - ' + this.toLabel(r.endMin);

    if (instructor && student) {
      first = student.user.lastname;
      second = instructor.user.lastname;
    } else if (instructor && !student) {
      first = instructor.user.lastname;
    } else if (!instructor && student) {
      first = student.user.lastname;
    }

    if (first) {
      returnString += '<br/>';
    }
    returnString += first.toUpperCase();

    if (second) {
      returnString += ' / ' + second.toUpperCase();
    }

    return returnString;
  }
}
